import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, Flame } from "lucide-react";

import { Button } from "@/components/ui/button";

/**
 * The brand mark, which is the way out to the landing: inside the app the
 * mark answers the same question it answers on the landing's own header, so
 * it leads to the page that explains the room. Getting back to home is not the mark's job — `HomeBack` beside it
 * is home on every screen that is a step inside, and the landing's header holds
 * a "Go home" button for the round trip.
 */
export function HomeLogo() {
  const { t } = useTranslation();
  return (
    <Link
      to="/"
      data-testid="home-logo"
      className="flex items-center gap-2.5 rounded-xl text-lg font-semibold tracking-tight text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-2xl border border-primary/40 bg-primary/12 text-primary shadow-glow-sm">
        <Flame className="size-4" aria-hidden="true" />
      </span>
      {t("app.name")}
    </Link>
  );
}

/**
 * The one way back (#832), beside the logo on every screen under home: the
 * page where a room is lit, the room an invitation answered, the profile. A
 * screen that is a step inside home goes back a step (`onBack`); one with an
 * address of its own goes home.
 */
export function HomeBack({ onBack }: { onBack?: (() => void) | undefined }) {
  const { t } = useTranslation();
  const label = (
    <>
      <ArrowLeft aria-hidden="true" />
      {t("action.back")}
    </>
  );
  return onBack ? (
    <Button variant="ghost" size="sm" data-testid="home-back" onClick={onBack}>
      {label}
    </Button>
  ) : (
    <Button asChild variant="ghost" size="sm" data-testid="home-back">
      <Link to="/home" search={{ room: undefined }}>
        {label}
      </Link>
    </Button>
  );
}
